import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { DashboardStats } from '../components/Dashboard/DashboardStats';
import { ActivityFeed } from '../components/Dashboard/ActivityFeed';
import { RevenueChart } from '../components/Dashboard/RevenueChart';
import { RevenueStreamsChart } from '../components/Dashboard/RevenueStreamsChart';
import { PageHeader, DateFilter, StatCard, StatCardSkeleton } from '../components/common';
import type { DateFilterValue } from '../components/common';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  AlertTitle,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  TrendingUp,
  Refresh,
  CheckCircle,
  Schedule,
  ErrorOutline,
  Notifications,
  Download,
  PictureAsPdf,
  TableChart,
  Description,
  LocationOn,
  CreditCard,
  BusinessCenter,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { useDashboardStats, useTransactions } from '../hooks';
import { colors, transitions, radius } from '../theme/theme';
import { useMutation } from '@tanstack/react-query';
import { transactionService } from '../services';
import { useSnackbar } from '../contexts/SnackbarContext';

export const DashboardPage = () => {
  const { showSnackbar } = useSnackbar();
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const handleDateFilterChange = (filter: DateFilterValue, start?: string, end?: string) => {
    setDateFilter(filter);
    if (start && end) {
      setStartDate(start);
      setEndDate(end);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: (format: 'csv' | 'excel' | 'pdf') =>
      transactionService.exportTransactions({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      }, format),
    onSuccess: (data: Blob, format: 'csv' | 'excel' | 'pdf') => {
      // Create blob with proper CSV type
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'csv' : format; // All formats export as CSV for now
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Export completed successfully', 'success');
      handleExportClose();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to export transactions';
      showSnackbar(message, 'error');
      handleExportClose();
    },
  });

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    exportMutation.mutate(format);
  };

  const filters = dateFilter === 'custom' && startDate && endDate
    ? { date_filter: dateFilter, start_date: startDate, end_date: endDate }
    : dateFilter !== 'all'
    ? { date_filter: dateFilter }
    : undefined;

  const { stats, isLoading } = useDashboardStats(filters);
  const { transactions } = useTransactions({ page: 1, per_page: 5 });

  // Use real data from API or fallback to empty arrays
  const recentTransactions = stats?.recent_transactions || transactions?.data || [];
  const revenueStreamsData = stats?.revenue_streams || [];
  const revenueOverviewData = stats?.revenue_overview || [];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // DataGrid columns definition
  const transactionColumns: GridColDef[] = [
    {
      field: 'transaction_reference',
      headerName: 'Reference',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'touch_point',
      headerName: 'Touch Point',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => {
        const touchPoint = row.touch_point?.touch_point || 'N/A';
        return touchPoint.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: radius.sm,
              bgcolor: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${colors.primary.main}40`,
            }}
          >
            <LocationOn sx={{ fontSize: '1rem', color: '#fff' }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'payment_module',
      headerName: 'Payment Module',
      flex: 1,
      minWidth: 170,
      valueGetter: (value, row) => row.payment_module?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: radius.sm,
              bgcolor: colors.success.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${colors.success.main}40`,
            }}
          >
            <CreditCard sx={{ fontSize: '1rem', color: '#fff' }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'service',
      headerName: 'Service',
      flex: 1,
      minWidth: 190,
      valueGetter: (value, row) => row.charge?.service?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: radius.sm,
              bgcolor: colors.warning.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${colors.warning.main}40`,
            }}
          >
            <BusinessCenter sx={{ fontSize: '1rem', color: '#fff' }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 140,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
          KES {Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : 'N/A'}
          color={getStatusColor(params.value)}
          size="small"
          sx={{
            textTransform: 'capitalize',
            fontWeight: 600,
            borderRadius: 2,
          }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 150,
      valueFormatter: (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
  ];

  // TODO: Replace with real activity feed from API
  // For now, using placeholder data
  const mockActivities = [
    {
      id: '1',
      action: 'Transaction Completed',
      user: 'System',
      created_at: 'Recently',
      type: 'success' as const,
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Dashboard"
          subtitle="Welcome back! Here's an overview of your county's activities."
          action={
            <Box sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' }
            }}>
              <DateFilter
                value={dateFilter}
                onChange={handleDateFilterChange}
                startDate={startDate}
                endDate={endDate}
              />
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </Box>
          }
        />

        {dateFilter !== 'all' && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <AlertTitle>Filtered View</AlertTitle>
            {dateFilter === 'custom'
              ? `Showing data from ${startDate} to ${endDate}`
              : `Showing data for: ${dateFilter.replace('_', ' ')}`}
            <Button
              size="small"
              onClick={() => handleDateFilterChange('all')}
              sx={{ ml: 2 }}
            >
              Clear Filter
            </Button>
          </Alert>
        )}

        <DashboardStats />

        {/* Transaction Status Breakdown */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }}>
          {isLoading ? (
            <StatCardSkeleton count={4} />
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Successful"
                  value={stats?.successful_transactions || stats?.total_transactions || 0}
                  icon={<CheckCircle />}
                  color="#2ec4b6"
                  trend={{
                    value: '+12%',
                    isPositive: true,
                  }}
                  delay={0}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Pending"
                  value={stats?.pending_transactions || 0}
                  icon={<Schedule />}
                  color="#ff9f1c"
                  trend={{
                    value: '-3%',
                    isPositive: false,
                  }}
                  delay={100}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Failed"
                  value={stats?.failed_transactions || 0}
                  icon={<ErrorOutline />}
                  color="#e71d36"
                  trend={{
                    value: '-8%',
                    isPositive: true,
                  }}
                  delay={200}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Notifications"
                  value={stats?.unread_notifications || 0}
                  icon={<Notifications />}
                  color="#0dcaf0"
                  delay={300}
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* Revenue Chart - Full Width */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 1 } }}>
          <Grid size={{ xs: 12 }}>
            <RevenueChart
              data={revenueOverviewData}
              isLoading={isLoading}
              title="Revenue Overview"
              subtitle="Last 12 months"
            />
          </Grid>
        </Grid>

        {/* Revenue Streams & Activity Feed - Side by Side on Medium+ Screens */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <RevenueStreamsChart
              data={revenueStreamsData}
              isLoading={isLoading}
              title="Revenue Streams"
              subtitle="Breakdown by touch point"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ActivityFeed
              activities={mockActivities}
              isLoading={isLoading}
              maxItems={5}
            />
          </Grid>
        </Grid>

        {/* Recent Transactions - Full Width */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }}>
          <Grid size={{ xs: 12 }}>
            <Card
              sx={{
                borderRadius: 2,
                overflow: 'visible',
                border: `1px solid ${colors.grey[200]}`,
                height: '100%',
                background: colors.gradients.card,
                boxShadow: colors.shadows.base,
                transition: transitions.base,
                animation: 'slideInUp 0.8s ease-out',
                '@keyframes slideInUp': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
                '&:hover': {
                  boxShadow: colors.shadows.md,
                },
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: radius.sm,
                        background: colors.gradients.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TableChart sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: colors.grey[900] }}>
                        Recent Transactions
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[600], fontSize: '0.8125rem' }}>
                        Latest financial activity
                      </Typography>
                    </Box>
                  </Box>
                }
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      sx={{
                        background: colors.gradients.primary,
                        color: 'white',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: colors.shadows.button,
                        transition: transitions.base,
                        '&:hover': {
                          background: colors.gradients.primaryHover,
                          boxShadow: colors.shadows.buttonHover,
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                      onClick={() => {
                        // TODO: Navigate to transactions page
                        window.location.href = '/admin/transactions';
                      }}
                    >
                      View All Transactions
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExportClick}
                      sx={{
                        borderColor: colors.primary.main,
                        color: colors.primary.main,
                        fontWeight: 600,
                        transition: transitions.base,
                        '&:hover': {
                          borderColor: colors.primary.dark,
                          bgcolor: `${colors.primary.main}08`,
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      Export
                    </Button>
                    <Menu
                      anchorEl={exportAnchorEl}
                      open={exportMenuOpen}
                      onClose={handleExportClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem onClick={() => handleExport('excel')}>
                        <ListItemIcon>
                          <TableChart fontSize="small" sx={{ color: '#107c41' }} />
                        </ListItemIcon>
                        <ListItemText>Export as Excel</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => handleExport('csv')}>
                        <ListItemIcon>
                          <Description fontSize="small" sx={{ color: '#0dcaf0' }} />
                        </ListItemIcon>
                        <ListItemText>Export as CSV</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => handleExport('pdf')}>
                        <ListItemIcon>
                          <PictureAsPdf fontSize="small" sx={{ color: '#e71d36' }} />
                        </ListItemIcon>
                        <ListItemText>Export as PDF</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                }
                sx={{ pb: 1 }}
              />
              <Box sx={{ height: 450, width: '100%', px: 2, pb: 2 }}>
                <DataGrid
                  rows={recentTransactions}
                  columns={transactionColumns}
                  loading={isLoading}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 5, page: 0 },
                    },
                  }}
                  pageSizeOptions={[5, 10, 25]}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'white',
                    boxShadow: colors.shadows.sm,
                    '& .MuiDataGrid-main': {
                      borderRadius: 2,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[200]}`,
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      color: colors.grey[900],
                      minHeight: '56px !important',
                      maxHeight: '56px !important',
                      lineHeight: '56px !important',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      padding: '0 16px',
                    },
                    '& .MuiDataGrid-columnHeader:first-of-type': {
                      borderTopLeftRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeader:last-of-type': {
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      fontSize: '0.875rem',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-row': {
                      minHeight: '72px !important',
                      maxHeight: '72px !important',
                      transition: transitions.fast,
                      '&:hover': {
                        backgroundColor: colors.primary[50],
                        transform: 'scale(1.001)',
                        boxShadow: `0 2px 8px ${colors.primary.main}20`,
                      },
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      marginTop: '56px !important',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                      borderBottomLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                    },
                    '& .MuiTablePagination-root': {
                      color: colors.grey[900],
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </AdminLayout>
  );
};
